import { useCallback } from 'react';
import { timesService, appService } from 'services';
import { useDispatch } from 'react-redux';
import { showModal, hideModal } from 'actions/modalActions';
import { shouldIncludeSupervised } from 'utils/utils';
import { useTranslation } from 'react-i18next';

/**
 * Hook para manejar la exportación de datos de asistencia
 */
export default function useAttendanceExport() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const openExportModal = useCallback((token, filename) => {
    dispatch(
      showModal({
        modalProps: {
          open: true,
          title: t('times.exportModalTitle'),
          style: { width: "400px" },
          confirmText: t('common.download'),
          loadingText: t('common.downloading'),
          message: t('attendance.exportModalMessage'),
          closeModal: () => dispatch(hideModal()),
          confirmAction: async () => {
            try {
              const response = await appService.getExported(token);
              const url = window.URL.createObjectURL(response);
              const a = document.createElement("a");
              document.body.appendChild(a);
              a.href = url;
              a.download = filename;
              a.click();
              dispatch(hideModal());
            } catch (error) {
              console.error('Error al descargar el archivo:', error);
              dispatch(
                showModal({
                  modalProps: {
                    open: true,
                    title: t('common.error'),
                    message: t('attendance.downloadError'),
                    closeModal: () => dispatch(hideModal()),
                  },
                  modalType: "alert",
                })
              );
            }
          },
        },
        modalType: "confirm",
      })
    );
  }, [dispatch, t]);

  const openAlertModal = useCallback((message, title) => {
    dispatch(
      showModal({
        modalProps: {
          open: true,
          title,
          message,
          closeModal: () => dispatch(hideModal()),
        },
        modalType: "alert",
      })
    );
  }, [dispatch]);

  const exportToExcel = useCallback(async (options = {}) => {
    const { 
      includeAllWorkflows, 
      includeApprovedWorkflows, 
      timeRegistrationLog,
      filterApproved,
      notEnded,
      filterAlerts
    } = options;

    try {
      let token;
      let filename;

      if (timeRegistrationLog) {
        token = await timesService.exportLogs({
          employee_ids: options.employeeIds,
          start_from: options.startDate,
          start_to: options.endDate,
          group_id: options.groupId,
          pending_only: filterApproved,
          not_ended: notEnded,
          include_supervised: shouldIncludeSupervised()
        });
        filename = "auditoria_fichajes.xlsx";
      } else {
        token = await timesService.exportList({
          employee_ids: options.employeeIds,
          start_from: options.startDate,
          start_to: options.endDate,
          group_id: options.groupId,
          include_approved_workflows: includeApprovedWorkflows,
          include_all_workflows: includeAllWorkflows,
          pending_only: filterApproved,
          not_ended: notEnded,
          include_supervised: shouldIncludeSupervised()
        });
        filename = includeAllWorkflows ? "fichajes_ausencias.xlsx" : "fichajes.xlsx";
      }

      if (token.ok) {
        openExportModal(token.access_token, filename);
      } else {
        openAlertModal(
          t('times.exportError'),
          t('times.approveErrorTitle')
        );
      }
    } catch (error) {
      console.error('Error en la exportación:', error);
      openAlertModal(
        t('times.exportError'),
        t('times.approveErrorTitle')
      );
    }
  }, [openExportModal, openAlertModal, t]);

  return {
    exportToExcel
  };
}